// HoursPage — Crew Utilization analytics (redesign). Route stays /hours; nav
// label is "Crew Utilization". Office-wide (owner/admin/back_office).
//
// Read-only over time_entries + jobs.actual_installed_footage — NO migration,
// capture/KPI only (NO pay $; Phase 2b later). Surface:
//   1. Header — eyebrow title + Year <select> (drives the chart only).
//   2. KPI tiles (4, month-scoped) — Production / Throughput / Activity / Jobs.
//   3. Monthly LF+SF line chart (Financials MonthlyTrendChart structure).
//   4. Per-person Week+Month rollup table (sortable, click row → drawer).
//   5. CrewDrawer (right slide-in) — that person's jobs (lazy-fetched).
//
// LOAD-BEARING: per-crew footage is CREDITED (each crew gets a shared job's full
// footage); team footage is ACTUAL (distinct jobs once). The TEAM TOTAL row
// totals Travel + jobs/active-days ONLY — LF/SF team cells show "—" because
// credited footage can't be grand-totaled (the real team footage is the
// Production KPI tile). LF and SF are NEVER summed together.

import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  TrendingUp, Gauge, CalendarCheck, Briefcase,
  type LucideIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import type { CrewPeriodRow, TeamPeriodTotals, MonthlyFootagePoint } from '@gutterinv/shared';
import { getCrewUtilization, getCrewUtilizationSeries } from '../api/client.js';
import { qk } from '../lib/queryKeys.js';
import { isBlockingError } from '../lib/isBlockingError.js';
import {
  localISODate, weekRangeFor, monthRangeFor, snapWeekStart,
  clampWeekStart, clampWeekAnchor, clampMonth,
  prevWeek, nextWeek,
  isCurrentOrLaterWeek,
  formatWeekLabel, formatMonthLabel, MONTHS_FULL,
  type WeekStartCode,
} from '../lib/hours-range.js';
import { formatHours } from '../lib/time-entry-format.js';
import { throughput, avgFootagePerJob, workdaysBetween, utilizationRate } from '../lib/crew-utilization.js';
import { CHART_COLORS } from '../lib/chart-colors.js';
import { CrewDrawer } from '../components/CrewDrawer.js';
import { cn } from '../lib/utils.js';

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Compact footage label dropping a trailing .0 ("180" not "180.0"). */
function formatFootage(n: number): string {
  return String(Number(n.toFixed(2)));
}

/** Whole-number footage for tiles/throughput ("4,200"). */
function formatFootageInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

// Week-start dropdown — Mon-first ordering, Sunday last (familiar to a US office).
const WEEK_START_OPTIONS: WeekStartCode[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<WeekStartCode, string> = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
};

/** Prev/next chevron button for the week + month pickers. */
function NavBtn({
  dir, label, onClick, disabled,
}: {
  dir: 'prev' | 'next';
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function HoursPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const today = useMemo(() => localISODate(), []);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const availableYears = useMemo(
    () => [currentYear, currentYear - 1], // 2026, 2025 — 2024 has no crew data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Navigable + configurable windows. Clamp-on-read: a typed/bookmarked URL can
  // hold garbage or a future value, and disabled "next" buttons don't protect it.
  // weekStart is WINDOW-ONLY — it does NOT change the Mon–Sat capacity basis the
  // utilization rate uses (see KpiRow).
  const [weekStart, setWeekStart] = useState<WeekStartCode>(
    () => clampWeekStart(searchParams.get('weekStart')),
  );
  const [weekAnchor, setWeekAnchor] = useState<string>(
    () => clampWeekAnchor(searchParams.get('week'), clampWeekStart(searchParams.get('weekStart')), today),
  );
  const [monthSel, setMonthSel] = useState<string>(
    () => clampMonth(searchParams.get('month'), today),
  );

  const week = useMemo(() => weekRangeFor(weekAnchor, weekStart, today), [weekAnchor, weekStart, today]);
  const month = useMemo(() => monthRangeFor(monthSel, today), [monthSel, today]);
  const weekLabel = formatWeekLabel(week.from, week.to);
  const monthLabel = formatMonthLabel(monthSel);
  const weekAtNow = isCurrentOrLaterWeek(weekAnchor, weekStart, today);

  // Month + Year are derived from the single monthSel source of truth. The Year
  // pulldown drives BOTH the KPI period and the annual chart. monthSel is already
  // clamped (on read + in the handlers below), so monthIndex is always within the
  // year-aware option set → the <select> value is never out of range.
  const year = Number(monthSel.slice(0, 4));
  const monthIndex = Number(monthSel.slice(5, 7)) - 1;
  // Year-aware month options: past year → all 12; current year → Jan…current month
  // (no future months, which would have no data).
  const monthOptions = useMemo(() => {
    const count = year < currentYear ? 12 : currentMonthIndex + 1;
    return Array.from({ length: count }, (_, i) => i);
  }, [year, currentYear, currentMonthIndex]);

  const onYearChange = (nextYear: number) => {
    // Keep the same month if valid in the new year, else clamp to its latest valid month.
    setMonthSel(clampMonth(`${nextYear}-${String(monthIndex + 1).padStart(2, '0')}`, today));
  };
  const onMonthChange = (idx: number) => {
    setMonthSel(clampMonth(`${year}-${String(idx + 1).padStart(2, '0')}`, today));
  };

  // Changing the week-start day re-snaps the anchor to the new boundary (idempotent).
  const onWeekStartChange = (code: WeekStartCode) => {
    setWeekStart(code);
    setWeekAnchor((a) => snapWeekStart(a, code));
  };

  // Open person reflected in the URL so refresh restores the drawer.
  const openCrewId = searchParams.get('crew');

  // Mirror view state into the URL (replace so back-button isn't polluted). Deps
  // deliberately exclude searchParams → no write→re-run loop; written values are
  // already normalized/snapped so refresh round-trips exactly.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('weekStart', weekStart);
    next.set('week', weekAnchor);
    next.set('month', monthSel); // encodes the year too — no separate ?year param
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, weekAnchor, monthSel]);

  const period = useQuery({
    queryKey: qk.crewUtilization.period(week.from, week.to, month.from, month.to),
    queryFn: () =>
      getCrewUtilization({
        weekFrom: week.from,
        weekTo: week.to,
        monthFrom: month.from,
        monthTo: month.to,
      }),
    // Keep the prior period's rows on screen during a week/month refetch so the
    // week control (embedded in the table) doesn't unmount/flicker. The body is
    // dimmed via isPlaceholderData while the new period loads.
    placeholderData: keepPreviousData,
  });

  const series = useQuery({
    queryKey: qk.crewUtilization.series(year),
    queryFn: () => getCrewUtilizationSeries(year),
  });

  const openPerson = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('crew', id);
    setSearchParams(next, { replace: false });
  };
  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('crew');
    setSearchParams(next, { replace: false });
  };

  // Fallback name for the drawer header while the detail loads.
  const fallbackName =
    period.data?.byCrewMonth.find((r) => r.crewMemberId === openCrewId)?.crewMemberName ??
    period.data?.byCrewWeek.find((r) => r.crewMemberId === openCrewId)?.crewMemberName;

  return (
    <div className="pt-3 space-y-8 max-w-6xl mx-auto">
      {/* Month + Year pulldowns (top-right) — drive the KPIs AND the annual chart. */}
      <header className="flex items-center justify-end gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Month</span>
          <select
            value={monthIndex}
            onChange={(e) => onMonthChange(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {monthOptions.map((idx) => (
              <option key={idx} value={idx}>{MONTHS_FULL[idx]}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Year</span>
          <select
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </header>

      {period.isPending && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {/* Only block on error when there's no cached data — a transient bg-refetch
          error keeps the existing KPIs/table (matches the app-wide isBlockingError
          treatment; works with the period query's keepPreviousData). */}
      {isBlockingError(period.error, !!period.data) && (
        <div className="text-sm text-destructive">
          Failed to load utilization: {period.error instanceof Error ? period.error.message : 'unknown error'}
        </div>
      )}

      {/* KPIs (direct child after the header → vertical anchor matches Financials'
          HeroTilesRow) + per-person table — both driven by the period query. */}
      {period.data && (
        <>
          <KpiRow
            team={period.data.teamMonth}
            crews={period.data.byCrewMonth}
            monthFrom={month.from}
            monthTo={month.to}
            monthLabel={monthLabel}
          />

          <PerPersonTable
            week={period.data.byCrewWeek}
            month={period.data.byCrewMonth}
            teamWeek={period.data.teamWeek}
            teamMonth={period.data.teamMonth}
            monthLabel={monthLabel}
            isStale={period.isPlaceholderData}
            weekControl={
              <div className="flex items-center justify-center gap-1.5 normal-case tracking-normal">
                <NavBtn dir="prev" label="Previous week" onClick={() => setWeekAnchor((a) => prevWeek(a))} />
                <span className="tnum min-w-[6rem] text-center text-sm font-medium text-foreground">{weekLabel}</span>
                <NavBtn dir="next" label="Next week" disabled={weekAtNow} onClick={() => setWeekAnchor((a) => nextWeek(a))} />
                <select
                  aria-label="Week starts on"
                  title="Week starts on"
                  value={weekStart}
                  onChange={(e) => onWeekStartChange(e.target.value as WeekStartCode)}
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm font-normal text-foreground"
                >
                  {WEEK_START_OPTIONS.map((c) => (
                    <option key={c} value={c}>{DAY_LABEL[c]}</option>
                  ))}
                </select>
              </div>
            }
            onOpenPerson={openPerson}
          />
        </>
      )}

      {/* Monthly chart — year-driven, independent of the period query (renders even
          while the period fetch is in flight). */}
      <MonthlyFootageChart
        year={year}
        data={series.data}
        isPending={series.isPending}
        error={series.error}
      />

      <CrewDrawer
        crewMemberId={openCrewId}
        fallbackName={fallbackName}
        from={month.from}
        to={month.to}
        rangeLabel={monthLabel}
        onClose={closeDrawer}
      />
    </div>
  );
}

// ============================================================
// KPI tiles (month-scoped).
// ============================================================
function KpiRow({
  team,
  crews,
  monthFrom,
  monthTo,
  monthLabel,
}: {
  team: TeamPeriodTotals;
  crews: CrewPeriodRow[];
  monthFrom: string;
  monthTo: string;
  monthLabel: string;
}) {
  // Throughput = ACTUAL team footage ÷ team active days (LF and SF separately).
  const lfPerDay = throughput(team.lfFootage, team.activeDays);
  const sfPerDay = throughput(team.sfFootage, team.activeDays);

  // Activity — headline the COUNT (team distinct active days). Avg active-days
  // per crew is the sub. Any rate is approximate, Mon–Sat-based, capped 100.
  const crewCount = crews.length;
  const avgActivePerCrew =
    crewCount > 0
      ? crews.reduce((acc, r) => acc + r.activeDays, 0) / crewCount
      : 0;
  // Capacity denominator is Mon–Sat BY DESIGN and independent of the page's
  // week-start setting (week-start changes only the week window, not capacity).
  const workdays = workdaysBetween(monthFrom, monthTo, true); // Mon–Sat
  const rate = utilizationRate(team.activeDays, workdays * Math.max(1, crewCount));

  // Jobs — distinct team jobs + avg ACTUAL footage per job (LF+SF combined for
  // the per-job average is NOT meaningful across units, so show LF and SF avg
  // side by side instead).
  const lfPerJob = avgFootagePerJob(team.lfFootage, team.jobCount);
  const sfPerJob = avgFootagePerJob(team.sfFootage, team.jobCount);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiTile
        label="Production"
        Icon={TrendingUp}
        value={`${formatFootageInt(team.lfFootage)} LF`}
        sub={`${formatFootageInt(team.sfFootage)} SF · installed · ${monthLabel}`}
      />
      <KpiTile
        label="Throughput"
        Icon={Gauge}
        value={`${formatFootageInt(lfPerDay)} LF/day`}
        sub={`${formatFootageInt(sfPerDay)} SF/day · per active day`}
      />
      <KpiTile
        label="Activity"
        Icon={CalendarCheck}
        value={`${team.activeDays} active days`}
        sub={
          rate === null
            ? `${avgActivePerCrew.toFixed(1)} avg days/crew`
            : `${avgActivePerCrew.toFixed(1)} avg days/crew · ~${Math.round(rate)}% (approx)`
        }
      />
      <KpiTile
        label="Jobs"
        Icon={Briefcase}
        value={`${team.jobCount} jobs`}
        sub={`${formatFootageInt(lfPerJob)} LF / ${formatFootageInt(sfPerJob)} SF avg per job`}
      />
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  Icon,
}: {
  label: string;
  value: string;
  sub: string;
  Icon: LucideIcon;
}) {
  return (
    <div className="group relative rounded-lg bg-card border border-border p-5 pb-4 overflow-hidden shadow-[0_1px_2px_rgba(27,42,63,0.05)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(27,42,63,0.12)]">
      <Icon className="absolute top-4 right-4 h-5 w-5 text-taupe/60" />
      <div className="eyebrow">{label}</div>
      <div className="text-2xl font-display font-semibold mt-2 tnum text-foreground">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

// ============================================================
// Monthly LF+SF chart — Financials MonthlyTrendChart structure.
// ============================================================
function MonthlyFootageChart({
  year,
  data,
  isPending,
  error,
}: {
  year: number;
  data: MonthlyFootagePoint[] | undefined;
  isPending: boolean;
  error: unknown;
}) {
  const chartData = useMemo(() => {
    const byMonth = new Map<number, MonthlyFootagePoint>();
    for (const p of data ?? []) byMonth.set(p.month, p);
    return SHORT_MONTHS.map((label, idx) => {
      const p = byMonth.get(idx + 1);
      return {
        month: label,
        lfFootage: p?.lfFootage ?? 0,
        sfFootage: p?.sfFootage ?? 0,
      };
    });
  }, [data]);

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Installed footage — {year}</h3>
      </div>
      <div className="h-80">
        {isPending ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
        ) : isBlockingError(error, !!data) ? (
          <div className="h-full flex items-center justify-center text-sm text-destructive">
            Failed to load chart
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="month" fontSize={11} stroke={CHART_COLORS.axis} />
              <YAxis
                fontSize={11}
                stroke={CHART_COLORS.axis}
                width={56}
                tickFormatter={(v: number) => (v == null ? '' : formatFootageInt(v))}
              />
              <Tooltip
                content={(props) => {
                  const { active, payload, label } = props;
                  if (!active || !payload || payload.length === 0) return null;
                  return (
                    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md space-y-1">
                      <div className="font-medium mb-1">{String(label ?? '')} {year}</div>
                      {payload.map((entry) => (
                        <div key={String(entry.dataKey)} style={{ color: String(entry.color ?? '') }}>
                          {String(entry.name)}: {formatFootageInt(Number(entry.value ?? 0))}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
              <Line type="monotone" dataKey="lfFootage" name="LF (gutter)" stroke={CHART_COLORS.lf} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              <Line type="monotone" dataKey="sfFootage" name="SF (insulation)" stroke={CHART_COLORS.sf} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Per-person table — Person | (Week: Travel·LF·SF) | (Month: Travel·LF·SF).
// Sortable, click row → drawer. TEAM TOTAL row totals Travel + jobs/active-days
// only; LF/SF team cells show "—".
// ============================================================
type SortDir = 'asc' | 'desc';
type SortKey =
  | 'name'
  | 'weekTravel' | 'weekLf' | 'weekSf'
  | 'monthTravel' | 'monthLf' | 'monthSf';

interface MergedRow {
  crewMemberId: string;
  crewMemberName: string;
  week: CrewPeriodRow | null;
  month: CrewPeriodRow | null;
}

function PerPersonTable({
  week,
  month,
  teamWeek,
  teamMonth,
  monthLabel,
  weekControl,
  isStale,
  onOpenPerson,
}: {
  week: CrewPeriodRow[];
  month: CrewPeriodRow[];
  teamWeek: TeamPeriodTotals;
  teamMonth: TeamPeriodTotals;
  monthLabel: string;
  /** Live week scroller (◀ label ▶ + start-day), rendered in the Week group header. */
  weekControl: ReactNode;
  /** True while the period query shows the PREVIOUS period's rows (refetching). */
  isStale?: boolean;
  onOpenPerson: (id: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('monthTravel');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const merged = useMemo<MergedRow[]>(() => {
    const map = new Map<string, MergedRow>();
    for (const r of week) {
      map.set(r.crewMemberId, {
        crewMemberId: r.crewMemberId,
        crewMemberName: r.crewMemberName,
        week: r,
        month: null,
      });
    }
    for (const r of month) {
      const existing = map.get(r.crewMemberId);
      if (existing) existing.month = r;
      else
        map.set(r.crewMemberId, {
          crewMemberId: r.crewMemberId,
          crewMemberName: r.crewMemberName,
          week: null,
          month: r,
        });
    }
    return [...map.values()];
  }, [week, month]);

  const sorted = useMemo(() => {
    const val = (row: MergedRow): string | number => {
      switch (sortKey) {
        case 'name': return row.crewMemberName.toLowerCase();
        case 'weekTravel': return row.week?.travelHours ?? 0;
        case 'weekLf': return row.week?.lfFootage ?? 0;
        case 'weekSf': return row.week?.sfFootage ?? 0;
        case 'monthTravel': return row.month?.travelHours ?? 0;
        case 'monthLf': return row.month?.lfFootage ?? 0;
        case 'monthSf': return row.month?.sfFootage ?? 0;
      }
    };
    const copy = [...merged];
    copy.sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [merged, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  // Credited footage isn't grand-totalable → the Team row shows "—" for LF/SF;
  // the tooltip carries the explanation that used to live in the removed footnote.
  const creditedHint = "Credited footage isn't grand-totalable — see the Production tile";

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        {/* table-fixed + colgroup so columns don't shift when values/names change
            between periods. min-w floor keeps the embedded week control from being
            squeezed below its size (the container scrolls instead). */}
        <table className="w-full min-w-[880px] table-fixed text-sm">
          <colgroup>
            <col className="w-[34%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
          </colgroup>
          <thead>
            <tr className="bg-secondary/40 text-xs">
              <th className="px-4 py-2" />
              {/* Week group header holds the LIVE week scroller (not a static label). */}
              <th colSpan={3} className="px-4 py-2 text-center">{weekControl}</th>
              <ThGroup label={`Month · ${monthLabel}`} span={3} boundary />
            </tr>
            <tr className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <Th label="Person" sortKey="name" active={sortKey} dir={sortDir} onSort={onSort} align="left" />
              <Th label="Travel" sortKey="weekTravel" active={sortKey} dir={sortDir} onSort={onSort} />
              <Th label="LF" sortKey="weekLf" active={sortKey} dir={sortDir} onSort={onSort} />
              <Th label="SF" sortKey="weekSf" active={sortKey} dir={sortDir} onSort={onSort} />
              <Th label="Travel" sortKey="monthTravel" active={sortKey} dir={sortDir} onSort={onSort} boundary />
              <Th label="LF" sortKey="monthLf" active={sortKey} dir={sortDir} onSort={onSort} />
              <Th label="SF" sortKey="monthSf" active={sortKey} dir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody className={cn(isStale && 'opacity-50 transition-opacity')}>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No hours logged for the selected week or month
                </td>
              </tr>
            ) : (
              sorted.map((row) => (
                <tr
                  key={row.crewMemberId}
                  onClick={() => onOpenPerson(row.crewMemberId)}
                  className="border-t border-border/40 cursor-pointer hover:bg-accent/30 transition-colors"
                >
                  <td className="px-4 py-3 font-medium truncate">{row.crewMemberName}</td>
                  <Cell value={row.week?.travelHours} kind="hours" />
                  <Cell value={row.week?.lfFootage} kind="footage" />
                  <Cell value={row.week?.sfFootage} kind="footage" />
                  <Cell value={row.month?.travelHours} kind="hours" boundary />
                  <Cell value={row.month?.lfFootage} kind="footage" />
                  <Cell value={row.month?.sfFootage} kind="footage" />
                </tr>
              ))
            )}
          </tbody>
          {sorted.length > 0 && (
            <tfoot className={cn(isStale && 'opacity-50 transition-opacity')}>
              <tr className="border-t border-border bg-secondary/20 font-medium">
                <td className="px-4 py-2">Team — actual installed (each job once)</td>
                <td className="tnum text-right px-4 py-2">{formatHours(teamWeek.travelHours)}</td>
                <td title={creditedHint} className="text-right px-4 py-2 text-muted-foreground">—</td>
                <td title={creditedHint} className="text-right px-4 py-2 text-muted-foreground">—</td>
                <td className="tnum text-right px-4 py-2 border-l border-border">{formatHours(teamMonth.travelHours)}</td>
                <td title={creditedHint} className="text-right px-4 py-2 text-muted-foreground">—</td>
                <td title={creditedHint} className="text-right px-4 py-2 text-muted-foreground">—</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// `boundary` marks the Week→Month split: a medium full-height rule (one logical
// column, set once and reused on every row) — NOT a heavy bar. The table stays a
// single roster with one shared sort; the divider groups columns, it doesn't
// promise independent per-half sorting.
function ThGroup({ label, span, boundary }: { label: string; span: number; boundary?: boolean }) {
  return (
    <th
      colSpan={span}
      className={cn(
        'px-4 py-1.5 text-center eyebrow text-[9px]',
        boundary ? 'border-l border-border' : 'border-l border-border/40',
      )}
    >
      {label}
    </th>
  );
}

function Th({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align = 'right',
  boundary,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  boundary?: boolean;
}) {
  const isActive = active === sortKey;
  return (
    <th className={cn(
      'px-4 py-2 font-semibold',
      align === 'left' ? 'text-left' : 'text-right',
      boundary && 'border-l border-border',
    )}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 transition-colors',
          align === 'right' && 'flex-row-reverse',
          isActive ? 'text-foreground font-bold' : 'hover:text-foreground',
        )}
      >
        {label}
        {isActive && (dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function Cell({
  value, kind, boundary,
}: {
  value: number | undefined;
  kind: 'hours' | 'footage';
  boundary?: boolean;
}) {
  const bound = boundary && 'border-l border-border';
  if (value === undefined) {
    return <td className={cn('text-right px-4 py-3 text-muted-foreground', bound)}>—</td>;
  }
  return (
    <td className={cn('tnum text-right px-4 py-3', bound)}>
      {kind === 'hours' ? formatHours(value) : formatFootage(value)}
    </td>
  );
}
